import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { applyTheme, GRAPHIKPRO_TOKENS } from "@/context/ThemeProvider";
import { setFleetOSFavicon } from "@/lib/favicon";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import {
  Command, ArrowRight, ArrowUpRight, Check, Radio, MapPin, ShieldCheck, BatteryLow,
  Clock, AlertTriangle, IndianRupee, CheckCircle2, UserCheck, CalendarPlus, RotateCcw,
  Truck, LayoutDashboard, Crosshair, Bike, Activity,
} from "lucide-react";

// Authentic, swappable real-world operations imagery (see /app/design_guidelines.json).
const IMG = {
  rider: "https://images.unsplash.com/photo-1695654390723-479197a8c4a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxmbGVldCUyMGRlbGl2ZXJ5JTIwcmlkZXJ8ZW58MHx8fHwxNzgxMjM5ODM2fDA&ixlib=rb-4.1.0&q=85",
  street: "https://images.unsplash.com/photo-1772633634752-7b7611e839fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxQb25kaWNoZXJyeSUyMHN0cmVldCUyMHNjb290ZXJ8ZW58MHx8fHwxNzgxMjM5ODM2fDA&ixlib=rb-4.1.0&q=85",
  handover: "https://images.unsplash.com/photo-1554223789-df81106a45ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxzY29vdGVyJTIwcmVudGFsJTIwaGFuZG92ZXJ8ZW58MHx8fHwxNzgxMjM5ODM3fDA&ixlib=rb-4.1.0&q=85",
};

const FEED = [
  { icon: CheckCircle2, tone: "live", title: "Vehicle handed over", sub: "PY01 AB 1234 · Rohan S." },
  { icon: UserCheck, tone: "live", title: "Rider verified", sub: "KYC passed · Delivery rider" },
  { icon: CalendarPlus, tone: "info", title: "New booking", sub: "Tourist · 3 days · Activa 6G" },
  { icon: RotateCcw, tone: "live", title: "Vehicle returned", sub: "PY05 R 9958 · on time" },
  { icon: Crosshair, tone: "info", title: "Daily check-in", sub: "Rider #214 · QR scanned" },
];

const ALERTS = [
  { icon: AlertTriangle, tone: "alert", title: "Geofence breach", sub: "KA05 XY 7766 · 2.4 km out" },
  { icon: Clock, tone: "warn", title: "Late return", sub: "PY02 KK 1180 · +45 min" },
  { icon: BatteryLow, tone: "warn", title: "Low battery", sub: "14% · Simple One" },
  { icon: IndianRupee, tone: "info", title: "Payment pending", sub: "₹2,400 · Invoice #1043" },
];

const TONE = {
  live: { text: "text-signal-live", dot: "#00FF66", ring: "ring-[#00FF66]/30", bg: "bg-[#00FF66]/10" },
  alert: { text: "text-signal-alert", dot: "#FF3B30", ring: "ring-[#FF3B30]/30", bg: "bg-[#FF3B30]/10" },
  warn: { text: "text-signal-warn", dot: "#FFCC00", ring: "ring-[#FFCC00]/30", bg: "bg-[#FFCC00]/10" },
  info: { text: "text-signal-info", dot: "#3B9EFF", ring: "ring-[#3B9EFF]/30", bg: "bg-[#3B9EFF]/10" },
};

const FEATURES = [
  { icon: LayoutDashboard, title: "Gated rider pipeline", body: "Nine non-negotiable KYC & handover gates. No bike leaves the lot until every gate passes.", span: "lg:col-span-7", lift: "lg:-mt-0" },
  { icon: MapPin, title: "Live GPS board", body: "Real telemetry, geofence breach alerts and trip trails on a live operations map.", span: "lg:col-span-5", lift: "lg:mt-10", photo: true },
  { icon: ShieldCheck, title: "Risk & recovery engine", body: "Per-tenant risk scoring sets deposits; a stationary-only escalation ladder protects assets.", span: "lg:col-span-5", lift: "lg:-mt-6" },
  { icon: Truck, title: "Delivery-rider vertical", body: "Gig riders on daily QR check-ins, multi-tier pricing, idle penalties and out/in condition.", span: "lg:col-span-7", lift: "lg:mt-4" },
  { icon: IndianRupee, title: "Billing & dunning", body: "Recurring auto-invoicing, grace periods and zero-commission UPI-QR collection.", span: "lg:col-span-7", lift: "lg:-mt-4" },
  { icon: Command, title: "White-label, multi-tenant", body: "One codebase, every operator branded as their own — isolated at the data layer.", span: "lg:col-span-5", lift: "lg:mt-6" },
];

const PLANS = [
  { name: "Starter", price: "4,999", tagline: "Single-location operators", features: ["Up to 25 vehicles", "5 staff seats", "Live telemetry board", "Tourist self-booking"] },
  { name: "Pro", price: "12,999", tagline: "Growing multi-vertical fleets", featured: true, features: ["Up to 150 vehicles", "25 staff seats", "Risk engine + recovery", "Maintenance · business · delivery", "Geofencing & per-km billing"] },
  { name: "Enterprise", price: "29,999", tagline: "Large branded operations", features: ["Up to 1,000 vehicles", "200 staff seats", "Every capability included", "Priority onboarding & support"] },
];

const METRICS = [
  { label: "Vehicles managed", value: 140, suffix: "+" },
  { label: "Rentals completed", value: 9200, suffix: "+" },
  { label: "GPS events tracked", value: 1.8, suffix: "M", decimals: 1 },
  { label: "Revenue processed", value: 78, prefix: "₹", suffix: " L", decimals: 0 },
];

/* ---------------- live streaming widgets (fixed slots, per-slot crossfade — overlap-proof) ---------------- */
const VISIBLE = 3;
const ROW_H = 56;

function useStream(items, interval) {
  const [list, setList] = useState(() => items.slice(0, VISIBLE).map((it, i) => ({ ...it, key: `init-${i}` })));
  const idx = useRef(VISIBLE % items.length);
  useEffect(() => {
    const t = setInterval(() => {
      setList((prev) => {
        const next = { ...items[idx.current % items.length], key: `${Date.now()}-${Math.random()}` };
        idx.current += 1;
        return [next, ...prev].slice(0, VISIBLE);
      });
    }, interval);
    return () => clearInterval(t);
  }, [items, interval]);
  return list;
}

function StreamRow({ item }) {
  const tone = TONE[item.tone];
  const Icon = item.icon;
  return (
    <div className="flex h-full items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3">
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${tone.bg} ${tone.ring}`}>
        <Icon className={`h-4 w-4 ${tone.text}`} strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-white">{item.title}</p>
        <p className="truncate text-[11px] text-white/45">{item.sub}</p>
      </div>
      <span className="shrink-0 text-[10px] tabular-nums text-white/30">now</span>
    </div>
  );
}

function StreamPanel({ title, dotClass, items, interval, testid }) {
  const stream = useStream(items, interval);
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[hsl(240,10%,7%)]/80 p-3 backdrop-blur-md">
      <div className="mb-2.5 flex items-center gap-2 px-1">
        <span className={`h-2 w-2 rounded-full ${dotClass}`} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">{title}</span>
      </div>
      {/* each slot is its own clipped box that swaps content via mode="wait" → two rows can never coexist */}
      <div className="space-y-2">
        {Array.from({ length: VISIBLE }).map((_, i) => {
          const it = stream[i];
          return (
            <div key={i} className="relative overflow-hidden rounded-xl" style={{ height: ROW_H }}>
              <AnimatePresence initial={false} mode="wait">
                {it && (
                  <motion.div
                    key={it.key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 }}
                    className="absolute inset-0"
                  >
                    <StreamRow item={it} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- animated SVG city map ---------------- */
function LiveMap() {
  const routes = [
    "M40,250 C110,210 150,150 230,150 S330,120 380,70",
    "M60,90 C130,120 180,200 270,210 S360,250 410,250",
    "M30,160 C120,150 200,80 300,110 S380,180 420,150",
  ];
  return (
    <svg viewBox="0 0 440 300" className="h-full w-full" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="trail" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00FF66" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#00FF66" stopOpacity="0.9" />
        </linearGradient>
        <radialGradient id="vignette" cx="50%" cy="45%" r="75%">
          <stop offset="60%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.55" />
        </radialGradient>
        <filter id="glow"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>

      {/* faint street grid */}
      <g stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => <line key={`v${i}`} x1={i * 55} y1="0" x2={i * 55} y2="300" />)}
        {Array.from({ length: 6 }).map((_, i) => <line key={`h${i}`} x1="0" y1={i * 55} x2="440" y2={i * 55} />)}
      </g>
      {/* major roads */}
      <g stroke="#ffffff" strokeOpacity="0.12" strokeWidth="2.5" fill="none">
        <path d="M0,210 C120,200 200,150 440,170" />
        <path d="M70,0 C90,120 60,220 120,300" />
      </g>

      {/* geofence */}
      <g transform="translate(230,150)">
        <circle r="88" fill="#00FF66" fillOpacity="0.04" stroke="#00FF66" strokeOpacity="0.25" strokeWidth="1.5" strokeDasharray="3 7" />
        <circle r="88" className="animate-radar" fill="none" stroke="#00FF66" strokeOpacity="0.12" strokeWidth="40" strokeDasharray="20 600" />
      </g>

      {/* route trails + moving vehicles */}
      {routes.map((d, i) => (
        <g key={i}>
          <path d={d} fill="none" stroke="url(#trail)" strokeWidth="2.5" strokeLinecap="round"
            strokeDasharray="10 12" className="animate-dashflow" style={{ animationDelay: `${i * 0.4}s` }} />
          <circle r="4" fill="#00FF66" filter="url(#glow)">
            <animateMotion dur={`${7 + i * 1.5}s`} repeatCount="indefinite" path={d} rotate="auto" />
          </circle>
        </g>
      ))}

      {/* location markers */}
      {[["#00FF66", 230, 150], ["#FF3B30", 380, 70], ["#FFCC00", 60, 90], ["#3B9EFF", 410, 250]].map(([c, x, y], i) => (
        <g key={i} transform={`translate(${x},${y})`}>
          <circle r="6" fill={c} fillOpacity="0.25" className="animate-ping-soft" style={{ transformOrigin: "center" }} />
          <circle r="3.5" fill={c} filter="url(#glow)" />
        </g>
      ))}
      <rect width="440" height="300" fill="url(#vignette)" />
    </svg>
  );
}

/* ---------------- command console ---------------- */
function CommandConsole() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
      data-testid="command-console"
      className="surface-tactile relative overflow-hidden rounded-[26px] border-white/10 bg-[hsl(240,10%,6%)] p-2 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)]"
    >
      {/* console header */}
      <div className="flex items-center justify-between rounded-t-[18px] border-b border-white/10 px-4 py-2.5">
        <div className="flex items-center gap-2 text-white">
          <Command className="h-4 w-4" strokeWidth={1.75} />
          <span className="text-sm font-semibold">FleetOS</span>
          <span className="text-xs text-white/40">· Live board</span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#00FF66]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-signal-live">
          <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00FF66] opacity-60" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00FF66]" /></span>
          Live
        </span>
      </div>

      {/* console body: feed · map · alerts */}
      <div className="grid gap-2 p-2 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.5fr)_minmax(0,0.9fr)]">
        <StreamPanel testid="live-feed" title="Live operations" dotClass="bg-[#00FF66]" items={FEED} interval={2300} />
        <div className="relative min-h-[210px] overflow-hidden rounded-2xl border border-white/10 bg-black lg:min-h-0">
          <img src={IMG.rider} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
          <div className="absolute inset-0"><LiveMap /></div>
          <div className="absolute bottom-2 left-2 flex gap-1.5 text-[10px]">
            {[["Active", "#00FF66"], ["On-trip", "#3B9EFF"], ["Alert", "#FF3B30"]].map(([l, c]) => (
              <span key={l} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/50 px-2 py-0.5 text-white/60 backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{l}
              </span>
            ))}
          </div>
        </div>
        <StreamPanel testid="alerts-panel" title="Active alerts" dotClass="bg-[#FF3B30]" items={ALERTS} interval={2900} />
      </div>

      {/* console footer metrics */}
      <div className="grid grid-cols-3 gap-px overflow-hidden rounded-b-[18px] border-t border-white/10 bg-white/5 text-center">
        {[["42", "vehicles live"], ["7", "on trip now"], ["3", "open alerts"]].map(([n, l]) => (
          <div key={l} className="bg-[hsl(240,10%,6%)] py-2.5">
            <p className="text-lg font-bold tracking-tight text-white">{n}</p>
            <p className="text-[10px] uppercase tracking-wider text-white/40">{l}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ---------------- count-up metric ---------------- */
function Metric({ value, label, prefix = "", suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf; const t0 = performance.now(); const dur = 1600;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setV(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  const display = decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString("en-IN");
  return (
    <div ref={ref} data-testid={`metric-${label}`}>
      <p className="whitespace-nowrap font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">{prefix}{display}{suffix}</p>
      <p className="mt-1.5 text-sm text-white/50">{label}</p>
    </div>
  );
}

/* ---------------- page ---------------- */
function Nav() {
  return (
    <nav className="sticky top-0 z-50 border-b border-white/10 bg-black/50 backdrop-blur-xl">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-3.5 sm:px-6 lg:px-8 lg:py-4">
        <div className="flex min-w-0 items-center gap-2 font-semibold text-white">
          <Command className="h-5 w-5 shrink-0" strokeWidth={1.75} />
          <span className="flex min-w-0 flex-col leading-tight sm:flex-row sm:items-baseline sm:gap-1.5 sm:leading-none">
            <span className="truncate">FleetOS</span>
            <span className="truncate text-[11px] font-normal text-white/40 sm:text-sm">by Graphikpro</span>
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3 sm:gap-5">
          <a href="#platform" className="hidden text-sm font-medium text-white/55 transition-colors hover:text-white sm:block">Platform</a>
          <a href="#proof" className="hidden text-sm font-medium text-white/55 transition-colors hover:text-white sm:block">Proof</a>
          <a href="#pricing" className="hidden text-sm font-medium text-white/55 transition-colors hover:text-white sm:block">Pricing</a>
          <Link to="/login" data-testid="nav-login" className="whitespace-nowrap text-sm font-medium text-white/70 transition-colors hover:text-white">Sign in</Link>
          <Button asChild className="whitespace-nowrap rounded-full bg-white px-3.5 text-sm text-black hover:bg-white/90 sm:px-5">
            <a href="mailto:hello@graphikpro.io?subject=FleetOS%20demo" data-testid="book-demo-cta">Book a demo</a>
          </Button>
        </div>
      </div>
    </nav>
  );
}

export default function FleetOSLanding() {
  useEffect(() => { applyTheme(GRAPHIKPRO_TOKENS); setFleetOSFavicon(); }, []);
  useDocumentTitle("Run your rental fleet on autopilot — FleetOS by Graphikpro", true);

  return (
    <div data-testid="fleetos-landing" className="min-h-screen bg-[hsl(240,10%,4%)] text-white antialiased">
      <Nav />

      {/* ===== Command-center hero ===== */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute inset-0 hero-grid opacity-40" />
          <div className="absolute -left-32 top-0 h-[30rem] w-[30rem] rounded-full bg-[#00FF66]/[0.06] blur-[100px]" />
          <div className="absolute right-0 top-32 h-96 w-96 rounded-full bg-[#3B9EFF]/[0.07] blur-[100px]" />
        </div>

        <div className="mx-auto w-full max-w-7xl px-6 pt-16 pb-20 lg:px-8 lg:pt-24">
          <div className="mx-auto max-w-3xl text-center">
            <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-white/70 backdrop-blur">
              <Radio className="h-3.5 w-3.5 text-signal-live" strokeWidth={2} /> Built and run inside a real rental business
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-6 font-semibold tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] sm:leading-[0.98]">
              Built by operators.<br /><span className="text-white/45">Built for operators.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/55 sm:text-lg">
              FleetOS is the command center for rental fleets — gated handovers, live GPS, delivery riders,
              risk recovery and automated billing. Watch your whole operation move, in real time.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" className="rounded-full bg-white px-8 text-black transition-transform hover:-translate-y-0.5 hover:bg-white/90">
                <a href="mailto:hello@graphikpro.io?subject=FleetOS%20demo" data-testid="hero-demo-cta">Book a demo <ArrowRight className="ml-1.5 h-4 w-4" /></a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/20 bg-transparent px-8 text-white transition-transform hover:-translate-y-0.5 hover:bg-white/5 hover:text-white">
                <Link to="/login" data-testid="hero-login-cta">Operator sign in</Link>
              </Button>
            </motion.div>
          </div>

          <div className="mx-auto mt-14 max-w-5xl">
            <CommandConsole />
          </div>
        </div>
      </section>

      {/* ===== Trust metrics band ===== */}
      <section id="proof" className="border-y border-white/10 bg-[hsl(240,10%,6%)]">
        <div className="mx-auto grid w-full max-w-7xl items-center gap-10 px-6 py-16 lg:grid-cols-[1fr_1.4fr] lg:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Customer zero</p>
            <h2 className="mt-3 font-semibold tracking-tight text-3xl md:text-4xl">Built and tested inside <span className="text-signal-live">VGR Rentals.</span></h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/50">Not a demo. Every feature is forged in a live electric-rental operation on the streets of Pondicherry — then handed to operators like you.</p>
          </div>
          <div className="grid grid-cols-2 gap-x-10 gap-y-10 sm:grid-cols-4 sm:gap-x-6 lg:gap-x-10">
            {METRICS.map((m) => <Metric key={m.label} {...m} />)}
          </div>
        </div>
      </section>

      {/* ===== Feature tension grid ===== */}
      <section id="platform" className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">The platform</p>
          <h2 className="mt-3 font-semibold tracking-tight text-3xl md:text-4xl lg:text-5xl">Everything a fleet runs on — in one operating system.</h2>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-12">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title} data-testid={`feature-${f.title}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className={`group relative overflow-hidden rounded-[22px] border border-white/10 bg-[hsl(240,10%,6.5%)] p-7 transition-colors hover:border-white/25 ${f.span} ${f.lift}`}
            >
              {f.photo && <img src={IMG.street} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.14] transition-opacity duration-500 group-hover:opacity-25" />}
              <div className="relative">
                <div className="flex h-11 w-11 items-center justify-center rounded-[13px] border border-white/10 bg-white/5 text-white"><f.icon className="h-5 w-5" strokeWidth={1.5} /></div>
                <h3 className="mt-4 font-semibold tracking-tight text-xl">{f.title}</h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-white/55">{f.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== Founder / real-ops story ===== */}
      <section className="mx-auto w-full max-w-7xl px-6 pb-24 lg:px-8 lg:pb-32">
        <div className="relative overflow-hidden rounded-[28px] border border-white/10">
          <img src={IMG.handover} alt="Real rental handover on a Pondicherry street" className="h-[420px] w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(240,10%,4%)] via-[hsl(240,10%,4%)]/70 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-8 lg:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Founder-led</p>
            <blockquote className="mt-3 max-w-2xl font-semibold tracking-tight text-2xl md:text-3xl leading-snug">
              "We didn't study fleets in a boardroom. We ran one — counted the bikes, chased the late returns, and built the software we wished existed."
            </blockquote>
            <p className="mt-4 text-sm text-white/55">The FleetOS team · operators first, builders second.</p>
          </div>
        </div>
      </section>

      {/* ===== Pricing ===== */}
      <section id="pricing" className="mx-auto w-full max-w-7xl px-6 pb-24 lg:px-8 lg:pb-32">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Pricing</p>
          <h2 className="mt-3 font-semibold tracking-tight text-3xl md:text-4xl">One platform fee. Every vertical included.</h2>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {PLANS.map((p) => (
            <div key={p.name} data-testid={`plan-${p.name}`}
              className={`relative overflow-hidden rounded-[22px] border bg-[hsl(240,10%,6.5%)] p-7 transition-all duration-300 hover:-translate-y-1 ${p.featured ? "border-[#00FF66]/40 shadow-[0_0_60px_-15px_rgba(0,255,102,0.35)]" : "border-white/10 hover:border-white/25"}`}>
              {p.featured && <span className="mb-3 inline-block rounded-full bg-[#00FF66]/15 px-3 py-1 text-xs font-semibold text-signal-live">Most popular</span>}
              <h3 className="font-semibold tracking-tight text-xl">{p.name}</h3>
              <p className="mt-1 text-sm text-white/50">{p.tagline}</p>
              <p className="mt-5"><span className="text-4xl font-bold tracking-tighter">₹{p.price}</span><span className="text-sm text-white/45">/month</span></p>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((feat) => <li key={feat} className="flex items-start gap-2 text-sm text-white/70"><Check className="mt-0.5 h-4 w-4 shrink-0 text-signal-live" strokeWidth={2} /> {feat}</li>)}
              </ul>
              <Button asChild className={`mt-7 w-full rounded-full ${p.featured ? "bg-[#00FF66] text-black hover:bg-[#00FF66]/90" : "bg-white/10 text-white hover:bg-white/20"}`}>
                <a href={`mailto:hello@graphikpro.io?subject=FleetOS%20${p.name}`}>Get started</a>
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Footer CTA ===== */}
      <footer className="relative overflow-hidden border-t border-white/10">
        <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[40rem] -translate-x-1/2 rounded-full bg-[#00FF66]/[0.06] blur-[100px]" />
        <div className="mx-auto w-full max-w-7xl px-6 py-24 text-center lg:px-8">
          <h2 className="mx-auto max-w-3xl font-semibold tracking-tighter text-4xl md:text-6xl">Put your fleet on the board.</h2>
          <p className="mx-auto mt-4 max-w-md text-white/55">Branded as you, run by us. Sales-to-live in minutes.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="rounded-full bg-white px-8 text-black transition-transform hover:-translate-y-0.5 hover:bg-white/90">
              <a href="mailto:hello@graphikpro.io?subject=FleetOS%20demo" data-testid="footer-cta">Book a demo <ArrowUpRight className="ml-1.5 h-4 w-4" /></a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/20 bg-transparent px-8 text-white hover:bg-white/5 hover:text-white">
              <Link to="/login">Operator sign in</Link>
            </Button>
          </div>
          <div className="mt-16 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-8 text-sm text-white/40 sm:flex-row">
            <span className="flex items-center gap-2 text-white/60"><Command className="h-4 w-4" /> FleetOS by Graphikpro</span>
            <span>© {new Date().getFullYear()} Graphikpro · Built in Pondicherry</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
